import { api } from '@most/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { qk } from './keys';
import type { BuyerOrderDetail, CartInput, CartState, SubmitOrderRequest } from './types';

/**
 * The cart lives SERVER-side (decision D14) so it survives across devices and syncs between tabs
 * over the websocket. The server also remains the authority on all the math — the client's
 * truck-fill gauge is for liveness only.
 */
export function useCart() {
  return useQuery({
    queryKey: qk.cart,
    queryFn: () => api.get<CartState>('/cart'),
    staleTime: 0,
  });
}

/** `PUT /cart` — full replace, re-priced by the server. */
export function usePutCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CartInput) => api.put<CartState>('/cart', input),
    onSuccess: (cart) => {
      queryClient.setQueryData(qk.cart, cart);
    },
  });
}

export function useClearCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>('/cart'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.cart });
    },
  });
}

export interface SubmitOrderArgs {
  request: SubmitOrderRequest;
  /**
   * REQUIRED by the contract. A double-clicked submit must not create two orders.
   *
   * A price-diff confirmation is a NEW attempt and needs a NEW key — reusing the key would make
   * the server replay the *rejected* attempt instead of accepting the new prices.
   */
  idempotencyKey: string;
}

/**
 * BUY-3 submit. Throws a `ProblemError` on 409, which the cart screen branches on:
 *   - `price-changed`        → modal with the per-line old→new diff, then resubmit with a NEW key
 *   - `cart-item-unavailable` → flag the named lines red, block submit until they are removed
 */
export function useSubmitOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ request, idempotencyKey }: SubmitOrderArgs) =>
      api.post<BuyerOrderDetail>('/orders', request, {
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
    onSuccess: () => {
      // The server empties the cart on submit; the order list and the bell both moved too.
      void queryClient.invalidateQueries({ queryKey: qk.cart });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: qk.me });
    },
  });
}

/** RFC 4122 v4. `crypto.randomUUID` is available in every browser the portal targets. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
